import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from './use-auth'
import * as eventosService from '@/services/eventos.service'
import type { Database } from '@/types/database'

type EventoInsert = Database['public']['Tables']['eventos']['Insert']
type EventoUpdate = Database['public']['Tables']['eventos']['Update']

// Hook para listar eventos
export function useEventos(params?: { status?: 'ativo' | 'encerrado'; busca?: string }) {
  const { perfil } = useAuth()

  return useQuery({
    queryKey: ['eventos', perfil?.igreja_id, params?.status, params?.busca],
    queryFn: () =>
      eventosService.listarEventos({
        igrejaId: perfil!.igreja_id!,
        status: params?.status,
        busca: params?.busca,
      }),
    enabled: !!perfil?.igreja_id,
  })
}

// Hook para buscar evento por slug
export function useEvento(slug: string | undefined) {
  return useQuery({
    queryKey: ['evento', slug],
    queryFn: () => eventosService.buscarEventoPorSlug(slug!),
    enabled: !!slug,
  })
}

// Hook para buscar equipe do evento
export function useEventoEquipe(eventoId: string | undefined) {
  return useQuery({
    queryKey: ['evento-equipe', eventoId],
    queryFn: () => eventosService.buscarEquipeEvento(eventoId!),
    enabled: !!eventoId,
  })
}

// Hook para criar evento
export function useCriarEvento() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (evento: EventoInsert) => eventosService.criarEvento(evento),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eventos'] })
    },
  })
}

// Hook para atualizar evento
export function useAtualizarEvento() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, evento }: { id: string; evento: EventoUpdate }) =>
      eventosService.atualizarEvento(id, evento),
    onSuccess: (eventoAtualizado) => {
      // Invalidar lista de eventos
      queryClient.invalidateQueries({ queryKey: ['eventos'] })

      // Invalidar por ID
      queryClient.invalidateQueries({ queryKey: ['evento', eventoAtualizado.id] })

      // Calcular e invalidar por slug
      if (eventoAtualizado.titulo && eventoAtualizado.inicio_em) {
        const slug = `${eventoAtualizado.titulo.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${new Date(eventoAtualizado.inicio_em).toISOString().split('T')[0]}`
        queryClient.invalidateQueries({ queryKey: ['evento', slug] })
      }

      // Invalidar equipe do evento
      queryClient.invalidateQueries({ queryKey: ['evento-equipe', eventoAtualizado.id] })
    },
  })
}

// Hook para deletar evento
export function useDeletarEvento() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => eventosService.deletarEvento(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eventos'] })
    },
  })
}

// Hook para adicionar membro à equipe
export function useAdicionarMembroEquipe() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (params: {
      eventoId: string
      usuarioId: string
      permissoes: {
        editar_evento?: boolean
        confirmar_pagamento?: boolean
        excluir_evento?: boolean
      }
    }) => eventosService.adicionarMembroEquipe(params),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['evento-equipe', variables.eventoId] })
    },
  })
}

// Hook para atualizar permissões
export function useAtualizarPermissoesEquipe() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (variables: {
      id: string
      eventoId: string
      permissoes: {
        editar_evento?: boolean
        confirmar_pagamento?: boolean
        excluir_evento?: boolean
      }
    }) => eventosService.atualizarPermissoesEquipe(variables.id, variables.permissoes),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['evento-equipe', variables.eventoId] })
    },
  })
}

// Hook para remover membro da equipe
export function useRemoverMembroEquipe() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (variables: { id: string; eventoId: string }) =>
      eventosService.removerMembroEquipe(variables.id),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['evento-equipe', variables.eventoId] })
    },
  })
}

// Hook para salvar perguntas
export function useSalvarPerguntasEvento() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      eventoId,
      perguntas,
    }: {
      eventoId: string
      perguntas: Array<{
        id?: string
        texto: string
        tipo: 'texto' | 'multipla_escolha' | 'sim_nao'
        obrigatoria: boolean
        ordem: number
      }>
    }) => eventosService.salvarPerguntasEvento(eventoId, perguntas),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['evento', variables.eventoId] })
    },
  })
}

// Hook para upload de capa
export function useUploadCapaEvento() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      file,
      igrejaId,
      eventoId,
    }: {
      file: File
      igrejaId: string
      eventoId: string
    }) => eventosService.uploadCapaEvento(file, igrejaId, eventoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eventos'] })
    },
  })
}

// Hook para buscar parcelas de um participante
export function useParcelasParticipante(participanteId: string | undefined) {
  return useQuery({
    queryKey: ['parcelas-participante', participanteId],
    queryFn: () => eventosService.buscarParcelasParticipante(participanteId!),
    enabled: !!participanteId,
  })
}

// Hook para marcar parcela como paga
export function useMarcarParcelaComoPaga() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (variables: {
      parcelaId: string
      participanteId: string
      metodo?: 'pix' | 'dinheiro' | 'cartao'
      comprovanteUrl?: string
      observacoes?: string
      dataPagamento?: string
      valor?: number
    }) => eventosService.marcarParcelaComoPaga(variables),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['parcelas-participante', variables.participanteId] })
    },
  })
}

// Hook para gerar parcelas automaticamente
export function useGerarParcelas() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (variables: {
      participanteId: string
      valorTotal: number
      numeroParcelas: number
      dataInicio: Date
    }) => eventosService.gerarParcelas(variables),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['parcelas-participante', variables.participanteId] })
    },
  })
}
